import _get from 'lodash/get';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import {
  getExcelTemplateEnpoint,
  getExcelTemplateV2Endpoint,
} from 'actions/bulk-upload-endpoints';
import { saveAs } from 'file-saver';
import moment from 'moment';

import {
  deliveriesDataToRows,
  getKeyColumnFormat,
  getUploadWorksheetFromWorkbook,
} from 'components/v2/anytime-booking/common/new-bulk-upload-common';
import {
  COLUMN_COD,
  COLUMN_ITEM_WEIGHT,
  COLUMN_NO,
  COLUMN_PARCEL,
  COLUMN_SERVICE_TYPE,
  COLUMN_VEHICLE_TYPE,
  COLUMN_MULTI_STOP_BATCH,
  COLUMN_MULTI_STOP_SEQUENCE_NUMBER,
  DATA_SHEET_NAME,
  KEY_ROW_INDEX,
  MAX_TEMPLATE_ROW,
  COLUMN_RECEIVER_ADMIN_AREA,
  COLUMN_SENDER_ADMIN_AREA,
  DATA_WORKSHEET_COL,
  COLUMN_PARCEL_SIZE,
} from 'constants/new-bulk-upload-const';
import { STORAGE_LOCALE } from 'constants/app-constants';
import { getDefaultErrorContext } from 'common/utils/rum-utils';

const getTemplateEndpoint = featureFlags => {
  const locale = window.localStorage.getItem(STORAGE_LOCALE);
  return _get(featureFlags, 'enableMSD', false)
    ? getExcelTemplateEnpoint(locale)
    : getExcelTemplateV2Endpoint(locale);
};

const parseColumnIndexes = (workSheet, featureFlags) => {
  const keyColumnFormat = getKeyColumnFormat(featureFlags);
  const row = workSheet.getRow(KEY_ROW_INDEX);
  row.eachCell((cell, colNumber) => {
    keyColumnFormat[cell.value].index = colNumber;
  });

  return keyColumnFormat;
};

const fillParcelTypesColumn = (dataWorkSheet, metaData) => {
  const parcels = _get(metaData, 'parcels', null);
  Object.values(parcels).forEach((parcel, index) => {
    dataWorkSheet.getCell(
      `${DATA_WORKSHEET_COL.ParcelTypes}${index + 2}`,
    ).value = parcel;
  });
};

const fillVehicleTypesColumn = (dataWorkSheet, metaData) => {
  Object.values(_get(metaData, 'vehicleTypeNames')).forEach(
    (vehicleType, index) => {
      dataWorkSheet.getCell(
        `${DATA_WORKSHEET_COL.VehicleTypes}${index + 2}`,
      ).value = vehicleType;
    },
  );
};

const fillServiceTypeColumn = (dataWorkSheet, metaData) => {
  Object.values(_get(metaData, 'serviceTypeNames')).forEach(
    (serviceType, index) => {
      dataWorkSheet.getCell(
        `${DATA_WORKSHEET_COL.ServiceTypes}${index + 2}`,
      ).value = serviceType;
    },
  );
};

const fillParcelSizeColumn = (dataWorkSheet, metaData) => {
  _get(metaData, 'parcelSizes').forEach(({ displaySize }, index) => {
    dataWorkSheet.getCell(
      `${DATA_WORKSHEET_COL.ParcelSizes}${index + 2}`,
    ).value = displaySize;
  });
};

const fillMaxWeightColumn = (dataWorkSheet, metaData) => {
  const maxWeight = _get(metaData, 'maxWeight', 0);
  for (let index = 1; index <= maxWeight; index++) {
    dataWorkSheet.getCell(
      `${DATA_WORKSHEET_COL.ItemWeights}${index + 1}`,
    ).value = index;
  }
};

const fillAdminAreasColumn = (dataWorkSheet, metaData) => {
  const adminAreas = _get(metaData, 'adminAreas');
  if (_isEmpty(adminAreas)) {
    return;
  }

  adminAreas.forEach(({ name }, index) => {
    dataWorkSheet.getCell(
      `${DATA_WORKSHEET_COL.AdminAreas}${index + 2}`,
    ).value = name;
  });
};

const genFormulae = (col, length) =>
  `${DATA_SHEET_NAME}!$${col}$2:$${col}$${length + 1}`;

const formatEachRow = (row, columnKeys, metaData) => {
  const parcels = _get(metaData, 'parcels', null);
  row.getCell(columnKeys[COLUMN_PARCEL].index).dataValidation = {
    type: 'list',
    allowBlank: true,
    formulae: [
      genFormulae(
        DATA_WORKSHEET_COL.ParcelTypes,
        Object.values(parcels).length,
      ),
    ],
  };

  const vehicleTypes = _get(metaData, 'vehicleTypes');
  row.getCell(columnKeys[COLUMN_VEHICLE_TYPE].index).dataValidation = {
    type: 'list',
    allowBlank: true,
    formulae: [
      genFormulae(DATA_WORKSHEET_COL.VehicleTypes, vehicleTypes.length),
    ],
  };

  const serviceTypes = _get(metaData, 'serviceTypes');
  row.getCell(columnKeys[COLUMN_SERVICE_TYPE].index).dataValidation = {
    type: 'list',
    allowBlank: true,
    formulae: [
      genFormulae(DATA_WORKSHEET_COL.ServiceTypes, serviceTypes.length),
    ],
  };

  const maxWeight = _get(metaData, 'maxWeight', 0);
  row.getCell(columnKeys[COLUMN_ITEM_WEIGHT].index).dataValidation = {
    type: 'list',
    allowBlank: true,
    formulae: [genFormulae(DATA_WORKSHEET_COL.ItemWeights, maxWeight)],
  };

  const adminAreas = _get(metaData, 'adminAreas');
  if (!_isEmpty(adminAreas)) {
    row.getCell(columnKeys[COLUMN_SENDER_ADMIN_AREA].index).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [genFormulae(DATA_WORKSHEET_COL.AdminAreas, adminAreas.length)],
    };
    row.getCell(columnKeys[COLUMN_RECEIVER_ADMIN_AREA].index).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [genFormulae(DATA_WORKSHEET_COL.AdminAreas, adminAreas.length)],
    };
  }

  const parcelSizes = _get(metaData, 'parcelSizes', []);
  row.getCell(columnKeys[COLUMN_PARCEL_SIZE].index).dataValidation = {
    type: 'list',
    allowBlank: true,
    formulae: [genFormulae(DATA_WORKSHEET_COL.ParcelSizes, parcelSizes.length)],
  };
};

const hideColumnWithMetaData = (workSheet, metaData, columnKeys) => {
  // Hide COD column
  const hasCodMetaData = _get(metaData, ['cod', 'currency', 'code'], null);
  if (!hasCodMetaData) {
    const codCol = workSheet.getColumn(columnKeys[COLUMN_COD].index);
    if (codCol) {
      codCol.hidden = true;
    }
  }

  // Hide MSD columns
  const isSupportMSD = _get(metaData, 'supportMSD', false);
  if (!isSupportMSD) {
    const multiStopCol = workSheet.getColumn(
      columnKeys[COLUMN_MULTI_STOP_BATCH].index,
    );
    if (multiStopCol) {
      multiStopCol.hidden = true;
    }
    const multiStopSequenceCol = workSheet.getColumn(
      columnKeys[COLUMN_MULTI_STOP_SEQUENCE_NUMBER].index,
    );
    if (multiStopSequenceCol) {
      multiStopSequenceCol.hidden = true;
    }
  }

  // hide admin area column if it's not needed
  const adminAreas = _get(metaData, 'adminAreas');
  if (_isEmpty(adminAreas)) {
    if (columnKeys[COLUMN_SENDER_ADMIN_AREA].index > -1) {
      workSheet.getColumn(
        columnKeys[COLUMN_SENDER_ADMIN_AREA].index,
      ).hidden = true;
    }
    if (columnKeys[COLUMN_RECEIVER_ADMIN_AREA].index > -1) {
      workSheet.getColumn(
        columnKeys[COLUMN_RECEIVER_ADMIN_AREA].index,
      ).hidden = true;
    }
  }
};

const fillDropdownColumnData = (dataWorkSheet, metaData) => {
  fillParcelTypesColumn(dataWorkSheet, metaData);
  fillVehicleTypesColumn(dataWorkSheet, metaData);
  fillServiceTypeColumn(dataWorkSheet, metaData);
  fillMaxWeightColumn(dataWorkSheet, metaData);
  fillAdminAreasColumn(dataWorkSheet, metaData);
  fillParcelSizeColumn(dataWorkSheet, metaData);
};

const parseBulkUploadTemplate = (
  workbook,
  metaDataWithVersion,
  itemCategories,
  bookingConfiguration,
  featureFlags,
) => {
  const workSheet = getUploadWorksheetFromWorkbook(workbook);
  const dataWorkSheet = workbook.getWorksheet(DATA_SHEET_NAME);
  const columnKeys = parseColumnIndexes(workSheet, featureFlags);
  const metaData = _get(metaDataWithVersion, 'meta');
  _set(
    metaData,
    'parcels',
    Object.fromEntries(itemCategories.map(({ id, name }) => [id, name])),
  );
  _set(
    metaData,
    'parcelSizes',
    _get(bookingConfiguration, ['parcelSizes'], []),
  );

  let bookingLimit = _get(metaData, 'bookingLimit');

  if (!bookingLimit || bookingLimit < 0) {
    bookingLimit = MAX_TEMPLATE_ROW;
  }
  const renderRowStartIndex = KEY_ROW_INDEX + 1;

  workSheet.duplicateRow(renderRowStartIndex, bookingLimit - 1, true);

  hideColumnWithMetaData(workSheet, metaData, columnKeys);

  fillDropdownColumnData(dataWorkSheet, metaData);

  for (let i = 0; i < bookingLimit; i++) {
    const row = workSheet.getRow(renderRowStartIndex + i);
    row.getCell(columnKeys[COLUMN_NO].index).value = i + 1;
    formatEachRow(row, columnKeys, metaData);
  }

  return workbook;
};

const bindToExcelRow = (exportRow, excelRow, columnKeys) => {
  Object.keys(exportRow).forEach(key => {
    excelRow.getCell(columnKeys[key].index).value = exportRow[key];
  });
};

const getOptimizedResult = ({
  deliveries,
  workbook,
  metaDataWithVersion,
  itemCategories,
  bookingConfiguration,
  featureFlags,
}) => {
  const exportRows = deliveriesDataToRows(deliveries, featureFlags);

  exportRows.sort((preRow, postRow) => {
    if (preRow[COLUMN_MULTI_STOP_BATCH] && postRow[COLUMN_MULTI_STOP_BATCH]) {
      if (
        preRow[COLUMN_MULTI_STOP_BATCH] === postRow[COLUMN_MULTI_STOP_BATCH]
      ) {
        return (
          preRow[COLUMN_MULTI_STOP_SEQUENCE_NUMBER] -
          postRow[COLUMN_MULTI_STOP_SEQUENCE_NUMBER]
        );
      }
      return preRow[COLUMN_MULTI_STOP_BATCH] - postRow[COLUMN_MULTI_STOP_BATCH];
    }

    if (preRow[COLUMN_MULTI_STOP_BATCH]) {
      return -1;
    }

    if (postRow[COLUMN_MULTI_STOP_BATCH]) {
      return 1;
    }

    return preRow[COLUMN_NO] - postRow[COLUMN_NO];
  });

  const workSheet = getUploadWorksheetFromWorkbook(workbook);
  const dataWorkSheet = workbook.getWorksheet(DATA_SHEET_NAME);
  const columnKeys = parseColumnIndexes(workSheet, featureFlags);
  const metaData = _get(metaDataWithVersion, 'meta');
  _set(
    metaData,
    'parcels',
    Object.fromEntries(itemCategories.map(({ id, name }) => [id, name])),
  );
  _set(
    metaData,
    'parcelSizes',
    _get(bookingConfiguration, ['parcelSizes'], []),
  );

  const renderRowStartIndex = KEY_ROW_INDEX + 1;

  workSheet.duplicateRow(renderRowStartIndex, exportRows.length - 1, true);

  hideColumnWithMetaData(workSheet, metaData, columnKeys);

  fillDropdownColumnData(dataWorkSheet, metaData);

  exportRows.forEach((exportRow, index) => {
    const excelRow = workSheet.getRow(renderRowStartIndex + index);
    bindToExcelRow(exportRow, excelRow, columnKeys);
    formatEachRow(excelRow, columnKeys, metaData);
  });

  return workbook;
};

export const downloadExcelTemplate = async (
  metaDataWithVersion,
  itemCategories,
  bookingConfiguration,
  featureFlags,
) => {
  try {
    const ExcelModule = await import('exceljs/dist/exceljs');
    const Excel = ExcelModule.default;

    const response = await fetch(getTemplateEndpoint(featureFlags));
    const arrayBuffer = await response.arrayBuffer();

    const workbook = new Excel.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    const newWorkBook = parseBulkUploadTemplate(
      workbook,
      metaDataWithVersion,
      itemCategories,
      bookingConfiguration,
      featureFlags,
    );

    const buffer = await newWorkBook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(blob, `GRABEXPRESS-${moment().format('DD/MM/YYYY')}.xlsx`);
  } catch (error) {
    const errorCtx = rumErrorCtx(getDefaultErrorContext())
      .component('downloadExcelTemplate')
      .message(error.message || 'Failed to download excel template')
      .severity(SEVERITY.MEDIUM)
      .build();
    ddRum.addError(error, errorCtx);
  }
};

export const downloadOptimizedResult = async ({
  deliveries,
  metaDataWithVersion,
  itemCategories,
  bookingConfiguration,
  featureFlags,
}) => {
  try {
    const ExcelModule = await import('exceljs/dist/exceljs');
    const Excel = ExcelModule.default;

    const response = await fetch(getTemplateEndpoint(featureFlags));
    const arrayBuffer = await response.arrayBuffer();

    const workbook = new Excel.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    const newWorkBook = getOptimizedResult({
      deliveries,
      workbook,
      metaDataWithVersion,
      itemCategories,
      bookingConfiguration,
      featureFlags,
    });

    const buffer = await newWorkBook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(
      blob,
      `GRABEXPRESS-OptimizedResult-${moment().format('DD/MM/YYYY')}.xlsx`,
    );
  } catch (error) {
    const errorCtx = rumErrorCtx(getDefaultErrorContext())
      .component('downloadOptimizedResult')
      .message(error.message || 'Failed to download optimized result')
      .severity(SEVERITY.MEDIUM)
      .build();
    ddRum.addError(error, errorCtx);
  }
};

export default {};
